import React, { useEffect, useState } from 'react'
import { Box, Typography, LinearProgress } from '@mui/material'
import { useGetScrapingStatusQuery } from 'graphql/generated/graphql'
import { CheckCircle2, Loader2 } from 'lucide-react'

interface CampaignLoadingProps {
  campaignId: number
  onComplete?: () => void
}

interface LoadingStage {
  label: string
  description: string
  targetProgress: number
}

const LOADING_STAGES: LoadingStage[] = [
  {
    label: 'Initializing',
    description: 'Setting up your campaign...',
    targetProgress: 10,
  },
  {
    label: 'Scraping Data',
    description: 'Collecting influencer data from social platforms...',
    targetProgress: 70,
  },
  {
    label: 'Analyzing',
    description: 'Processing and analyzing collected data...',
    targetProgress: 90,
  },
  {
    label: 'Finalizing',
    description: 'Preparing your campaign dashboard...',
    targetProgress: 100,
  },
]

export default function CampaignLoadingWithProgress({
  campaignId,
  onComplete,
}: CampaignLoadingProps): JSX.Element {
  const [currentProgress, setCurrentProgress] = useState(0)
  const [completedStages, setCompletedStages] = useState<number[]>([])
  const [hasStartedScraping, setHasStartedScraping] = useState(false)
  const [pollCount, setPollCount] = useState(0)

  // Poll scraping status every 2 seconds
  const { data, stopPolling, error } = useGetScrapingStatusQuery({
    variables: { 
      filter: { campaignId } 
    },
    pollInterval: 2000,
    fetchPolicy: 'network-only', // Always fetch fresh data
  })

  // Debug logging
  useEffect(() => {
    setPollCount(prev => prev + 1)
    console.log(`🔍 Poll #${pollCount} - Campaign ${campaignId}:`, {
      hasData: !!data,
      hasError: !!error,
      scraping: data?.scraping,
      error: error?.message,
    })
  }, [data, error, campaignId, pollCount])

  // Calculate real progress based on scraping status
  useEffect(() => {
    if (error) {
      console.error('❌ Scraping status query error:', error)
      // Optionally: show error state or fallback to time-based progress
      return
    }

    if (!data?.scraping) {
      console.log('⏳ Waiting for scraping data...')
      // Stay at initializing stage
      setCurrentProgress(5)
      return
    }

    const status = data.scraping
    console.log('📊 Platform Status:', {
      instagram: status.instagram,
      youtube: status.youtube,
      reddit: status.reddit,
      tiktok: status.tiktok,
      facebook: status.facebook,
    })

    const platforms = [
      status.instagram,
      status.youtube,
      status.reddit,
      status.tiktok,
      status.facebook,
    ]

    const totalPlatforms = platforms.length
    const scrapingPlatforms = platforms.filter(p => p).length
    const notScrapingPlatforms = platforms.filter(p => !p).length

    console.log('📈 Scraping Analysis:', {
      totalPlatforms,
      scrapingPlatforms,
      notScrapingPlatforms,
      hasStartedScraping,
    })

    // Track if scraping has ever started
    if (scrapingPlatforms > 0) {
      setHasStartedScraping(true)
    }

    let calculatedProgress = 0

    if (scrapingPlatforms === 0 && hasStartedScraping) {
      // All platforms finished scraping (were true before, now all false)
      console.log('✅ Scraping complete! Finalizing...')
      calculatedProgress = 90
      
      setTimeout(() => {
        setCurrentProgress(100)
        setTimeout(() => {
          console.log('🎉 Campaign complete! Calling onComplete...')
          stopPolling()
          onComplete?.()
        }, 500)
      }, 1000)
    } else if (scrapingPlatforms > 0) {
      // Scraping in progress
      const completedCount = totalPlatforms - scrapingPlatforms
      const progressPerPlatform = 60 / totalPlatforms
      const scrapingProgress = completedCount * progressPerPlatform
      calculatedProgress = 10 + scrapingProgress
      console.log(`⚙️ Scraping: ${completedCount}/${totalPlatforms} platforms done (${Math.round(calculatedProgress)}%)`)
    } else if (!hasStartedScraping) {
      // Scraping hasn't started yet - still initializing
      console.log('🔄 Initializing... scraping not started')
      calculatedProgress = 10
    }

    setCurrentProgress(calculatedProgress)

    // Mark stages as completed based on progress
    const completed: number[] = []
    LOADING_STAGES.forEach((stage, index) => {
      if (calculatedProgress >= stage.targetProgress) {
        completed.push(index)
      }
    })
    setCompletedStages(completed)
  }, [data, error, stopPolling, onComplete, hasStartedScraping])

  // Safety timeout: if stuck for too long, complete anyway
  useEffect(() => {
    const timeout = setTimeout(() => {
      console.log('⏱️ Timeout reached - completing anyway')
      setCurrentProgress(100)
      setTimeout(() => {
        stopPolling()
        onComplete?.()
      }, 500)
    }, 60000) // 60 seconds max

    return () => clearTimeout(timeout)
  }, [stopPolling, onComplete])

  // Determine current active stage
  const currentStageIndex = LOADING_STAGES.findIndex(
    (stage) => currentProgress < stage.targetProgress
  )
  const activeStage =
    currentStageIndex === -1
      ? LOADING_STAGES[LOADING_STAGES.length - 1]
      : LOADING_STAGES[currentStageIndex]

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        padding: '2rem',
      }}
    >
      <Box
        sx={{
          maxWidth: '600px',
          width: '100%',
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: '24px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          padding: '3rem',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', marginBottom: '3rem' }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              marginBottom: '1.5rem',
              animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
              '@keyframes pulse': {
                '0%, 100%': {
                  opacity: 1,
                },
                '50%': {
                  opacity: 0.5,
                },
              },
            }}
          >
            <Loader2 size={40} color="white" className="animate-spin" />
          </Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '0.5rem',
            }}
          >
            Building Your Campaign
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
            }}
          >
            {activeStage.description}
          </Typography>
          
          {/* Debug info - remove in production */}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              marginTop: '1rem',
              color: 'rgba(255, 255, 255, 0.4)',
              fontFamily: 'monospace',
            }}
          >
            Campaign ID: {campaignId} | Polls: {pollCount} | Has Data: {data?.scraping ? 'Yes' : 'No'}
          </Typography>
        </Box>

        {/* Progress Bar */}
        <Box sx={{ marginBottom: '3rem' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '0.5rem',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontWeight: 600,
              }}
            >
              {activeStage.label}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontWeight: 600,
              }}
            >
              {Math.round(currentProgress)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={currentProgress}
            sx={{
              height: '8px',
              borderRadius: '4px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '& .MuiLinearProgress-bar': {
                borderRadius: '4px',
                background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                transition: 'transform 0.4s ease-in-out',
              },
            }}
          />
        </Box>

        {/* Rest of your component stays the same... */}
        {/* Stage List */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {LOADING_STAGES.map((stage, index) => {
            const isCompleted = completedStages.includes(index)
            const isActive = activeStage === stage && !isCompleted
            const isPending = !isCompleted && !isActive

            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '1rem',
                  borderRadius: '12px',
                  background: isActive
                    ? 'rgba(102, 126, 234, 0.1)'
                    : 'rgba(255, 255, 255, 0.03)',
                  border: isActive
                    ? '1px solid rgba(102, 126, 234, 0.3)'
                    : '1px solid rgba(255, 255, 255, 0.05)',
                  transition: 'all 0.3s ease',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    flexShrink: 0,
                    background: isCompleted
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : isActive
                      ? 'rgba(102, 126, 234, 0.2)'
                      : 'rgba(255, 255, 255, 0.05)',
                  }}
                >
                  {isCompleted ? (
                    <CheckCircle2 size={18} color="white" />
                  ) : isActive ? (
                    <Loader2
                      size={18}
                      color="#667eea"
                      className="animate-spin"
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                      }}
                    />
                  )}
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: isCompleted || isActive
                        ? 'rgba(255, 255, 255, 0.95)'
                        : 'rgba(255, 255, 255, 0.5)',
                    }}
                  >
                    {stage.label}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: isCompleted || isActive
                        ? 'rgba(255, 255, 255, 0.7)'
                        : 'rgba(255, 255, 255, 0.4)',
                    }}
                  >
                    {stage.description}
                  </Typography>
                </Box>

                {isCompleted && (
                  <Typography
                    variant="caption"
                    sx={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      background: 'rgba(102, 126, 234, 0.2)',
                      color: '#667eea',
                      fontWeight: 600,
                    }}
                  >
                    Complete
                  </Typography>
                )}
              </Box>
            )
          })}
        </Box>

        {/* Real-time Platform Status */}
        {data?.scraping && (
          <Box
            sx={{
              marginTop: '2rem',
              padding: '1rem',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.05)',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255, 255, 255, 0.5)',
                display: 'block',
                marginBottom: '0.5rem',
              }}
            >
              Platform Status:
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: '0.5rem',
                flexWrap: 'wrap',
              }}
            >
              {[
                { name: 'Instagram', status: data.scraping.instagram },
                { name: 'YouTube', status: data.scraping.youtube },
                { name: 'Reddit', status: data.scraping.reddit },
                { name: 'TikTok', status: data.scraping.tiktok },
                { name: 'Facebook', status: data.scraping.facebook },
              ].map(({ name, status }) => {
                const isScraping = status
                const isComplete = !status && hasStartedScraping

                return (
                  <Box
                    key={name}
                    sx={{
                      padding: '4px 10px',
                      borderRadius: '8px',
                      background: isComplete
                        ? 'rgba(102, 126, 234, 0.2)'
                        : isScraping
                        ? 'rgba(234, 179, 8, 0.2)'
                        : 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid',
                      borderColor: isComplete
                        ? 'rgba(102, 126, 234, 0.3)'
                        : isScraping
                        ? 'rgba(234, 179, 8, 0.3)'
                        : 'rgba(255, 255, 255, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    {isComplete && (
                      <CheckCircle2 size={12} color="#667eea" />
                    )}
                    {isScraping && (
                      <Loader2
                        size={12}
                        color="#eab308"
                        className="animate-spin"
                      />
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        color: isComplete
                          ? '#667eea'
                          : isScraping
                          ? '#eab308'
                          : 'rgba(255, 255, 255, 0.5)',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                      }}
                    >
                      {name}
                    </Typography>
                  </Box>
                )
              })}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}